import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Asset } from "../models/asset";
import useAssets from "./useAssets";
import { fetchMaintenance } from "../api/fetchMaintenance";
import { fetchCheckupsByChassisList } from "../api/fetchCheckup";
import { getLastAssetMetrics } from "../utils/assetUtils";

function useAssetPeriodicReview() {
    const [searchParams] = useSearchParams();
    const { allAssets } = useAssets();

    const [periodicReviewSidebar, setPeriodicReviewSidebar] = useState(false);
    const [assetPeriodicReviews, setAssetPeriodicReview] = useState<Asset | null>(null);
    const [hasProcessedChassisParam, setHasProcessedChassisParam] = useState(false);

    const handlePeriodicReviewSidebar = (asset: Asset) => {
        setPeriodicReviewSidebar(!periodicReviewSidebar);
        setAssetPeriodicReview(asset);
    };

    const handleClosePeriodicReviewSidebar = () => {
        setPeriodicReviewSidebar(false);
        setAssetPeriodicReview(null);
    };

    useEffect(() => {
        if (hasProcessedChassisParam || !allAssets.length) return;

        const chassisParam = searchParams.get("chassis");
        const shouldOpen = searchParams.get("periodic-review-modal-open") === "true";
        if (!chassisParam || !shouldOpen) return;

        const cleanChassisParam = chassisParam.trim().toUpperCase();
        const matchedAsset = allAssets.find(
            (asset) => asset.chassis?.trim().toUpperCase() === cleanChassisParam
        );

        if (!matchedAsset) {
            setHasProcessedChassisParam(true);
            return;
        }

        setHasProcessedChassisParam(true);

        const openSidebarForChassis = async () => {
            const [assetsFetched] = await fetchMaintenance([
                { id: matchedAsset.id, identification: matchedAsset.chassis, name: matchedAsset.name },
            ]);
            if (!assetsFetched.length) return;

            let asset = assetsFetched[0];

            const checkups = await fetchCheckupsByChassisList([asset.chassis], [asset]);
            const checkupForAsset = checkups.find((el) => el.chassis === asset.chassis);

            if (checkupForAsset) {
                const metrics = getLastAssetMetrics(asset, checkupForAsset.previous ?? []);
                asset = {
                    ...asset,
                    odometer: metrics.odometer ?? asset.odometer,
                    lastOdometerUpdate: metrics.lastOdometerUpdate ?? asset.lastOdometerUpdate,
                    hourmeter: metrics.hourmeter ?? asset.hourmeter,
                    lastHourmeterUpdate: metrics.lastHourmeterUpdate ?? asset.lastHourmeterUpdate,
                    schedulings: {
                        previous: checkupForAsset.previous ?? [],
                        next: checkupForAsset.next ?? [],
                    },
                };
            }

            setAssetPeriodicReview(asset);
            setPeriodicReviewSidebar(true);
        };

        openSidebarForChassis();
    }, [allAssets, searchParams, hasProcessedChassisParam]);

    return { handlePeriodicReviewSidebar, handleClosePeriodicReviewSidebar, periodicReviewSidebar, assetPeriodicReviews };
}

export default useAssetPeriodicReview;
